import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetOnboardingChecklistQuery } from 'store/services/onboarding/onboardingApi';
import { useGetRecentArticlesQuery } from 'store/services/blog/blogApi';
import { useGetHomeActivityStatsQuery } from 'store/services/activity/activityApi';
import { useGetUserTradeStatsQuery } from 'store/services/profile/profileApi';
import { OnboardingTaskKey } from 'types/interfaces/onboarding';
import { trackEvent } from 'utils/analytics';
import BlogPreview from './components/BlogPreview/BlogPreview';
import CompletionModal from './components/CompletionModal/CompletionModal';
import WelcomeHeader from './components/WelcomeHeader/WelcomeHeader';
import SetupBanner from './components/SetupBanner/SetupBanner';
import LiveMapModule from './components/LiveMapModule/LiveMapModule';
import RecentActivitySection from './components/RecentActivitySection/RecentActivitySection';
import HomeLeaderboard from './components/HomeLeaderboard/HomeLeaderboard';
import { checklistPhoneVerified } from './components/SetupBanner/setupState';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import { useConfetti } from 'hooks/useConfetti';
import { SEOHelmet } from 'components/SEO';
import './HomePage.css';

// The homepage (Asana 1218957945501621, Decision 1): Welcome header, Setup
// Banner, Live map with its pulse, Recent Activity, then News beside the
// Leaderboard. Below 768px everything stacks in the same order.

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { triggerMilestone } = useConfetti();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const username = currentUser?.username || 'Trader';

  const { data: checklistData, refetch: refetchChecklist } = useGetOnboardingChecklistQuery(undefined, {
    // Always refetch when mounting to ensure fresh data after completing tasks elsewhere
    refetchOnMountOrArgChange: true,
  });
  const { data: recentArticles, isLoading: isArticlesLoading } = useGetRecentArticlesQuery(6);
  const { data: stats, isLoading: statsLoading } = useGetHomeActivityStatsQuery();
  const { data: tradeStats } = useGetUserTradeStatsQuery(username, { skip: !currentUser?.username });

  // Local state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showFirstClaimModal, setShowFirstClaimModal] = useState(false);
  const [firstClaimPoints, setFirstClaimPoints] = useState(0);
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);

  // Use refs to track previous values without causing re-renders
  const previousCompletedCountRef = useRef<number | null>(null);
  const previousCompletedTasksRef = useRef<Set<OnboardingTaskKey>>(new Set());
  const hasInitializedRef = useRef(false);

  // Track milestone achievements and auto-detected task completions
  useEffect(() => {
    if (checklistData?.summary && checklistData?.tasks) {
      const currentCount = checklistData.summary.completedTasks;
      const currentCompletedTasks = new Set(checklistData.tasks.filter((t) => t.status === 'COMPLETED').map((t) => t.taskKey));

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        previousCompletedCountRef.current = currentCount;
        previousCompletedTasksRef.current = currentCompletedTasks;
        return;
      }

      checklistData.tasks.forEach((task) => {
        if (task.status === 'COMPLETED' && task.detectionType === 'AUTO' && !previousCompletedTasksRef.current.has(task.taskKey)) {
          trackEvent('onboarding_task_detected', {
            task_key: task.taskKey,
            task_order: task.displayOrder,
            detection_type: 'auto',
          });
        }
      });

      const prevCount = previousCompletedCountRef.current ?? 0;
      if (prevCount < 5 && currentCount >= 5) {
        triggerMilestone('50_percent');
        trackEvent('onboarding_milestone', { milestone: '50_percent' });
      }
      if (prevCount < 10 && currentCount === 10) {
        setShowCompletionModal(true);
        triggerMilestone('all_complete');
        trackEvent('onboarding_milestone', { milestone: 'all_complete' });
      }

      previousCompletedCountRef.current = currentCount;
      previousCompletedTasksRef.current = currentCompletedTasks;
    }
  }, [checklistData, triggerMilestone]);

  // A claim landed in the banner: the first one ever gets the celebration modal
  const handleClaimed = useCallback(
    (taskKey: OnboardingTaskKey, points: number) => {
      const isFirstClaim = !localStorage.getItem('hasClaimedFirstReward');
      if (isFirstClaim) {
        setFirstClaimPoints(points);
        setShowFirstClaimModal(true);
        triggerMilestone('50_percent');
        localStorage.setItem('hasClaimedFirstReward', 'true');
        trackEvent('onboarding_first_claim', { task_key: taskKey, points_awarded: points });
      }
    },
    [triggerMilestone]
  );

  const handleCompletionModalClose = useCallback(() => setShowCompletionModal(false), []);
  const handleFirstClaimModalClose = useCallback(() => setShowFirstClaimModal(false), []);
  const handleStartTrading = useCallback(() => {
    setShowCompletionModal(false);
    navigate('/sendcards');
  }, [navigate]);
  const handleViewPoints = useCallback(() => {
    setShowCompletionModal(false);
    navigate('/transaction-history');
  }, [navigate]);
  const handlePhoneVerificationClose = useCallback(() => setShowPhoneVerificationModal(false), []);
  // verifyPhone already invalidates the checklist; the explicit refetch covers a stale cache
  const handlePhoneVerificationSuccess = useCallback(() => {
    refetchChecklist();
  }, [refetchChecklist]);

  const tasks = useMemo(() => checklistData?.tasks ?? [], [checklistData]);
  const taskByKey = useMemo(() => new Map(tasks.map((t) => [t.taskKey, t])), [tasks]);
  // The live phone flag from the payload, never the VERIFY_PHONE row (pass 7)
  const phoneVerified = checklistPhoneVerified(checklistData);
  const returning = (tradeStats?.completedTrades ?? 0) >= 1;
  const firstPointsReward = taskByKey.get('VERIFY_PHONE')?.pointsReward ?? 0;

  return (
    <>
      <SEOHelmet
        title="Home - TradeMagic"
        description="Your TradeMagic dashboard. See live trades on the map, finish your setup, and start trading Magic: The Gathering cards."
        canonical="https://trademagic.gg/home"
        type="website"
      />
      <div className="home-page">
        <div className="home-page-content">
          <WelcomeHeader
            username={username}
            returning={returning}
            stats={stats}
            showFirstPointsNudge={!!checklistData && !phoneVerified && firstPointsReward > 0}
            firstPointsReward={firstPointsReward}
            isLoading={statsLoading}
          />

          <SetupBanner onVerifyPhone={() => setShowPhoneVerificationModal(true)} onClaimed={handleClaimed} />

          <LiveMapModule />

          <RecentActivitySection />

          <div className="home-page-row">
            <BlogPreview articles={recentArticles ?? []} isLoading={isArticlesLoading} />
            <HomeLeaderboard />
          </div>
        </div>

        <CompletionModal
          isOpen={showCompletionModal}
          onClose={handleCompletionModalClose}
          totalPointsEarned={checklistData?.summary?.totalPointsEarned ?? 1600}
          onStartTrading={handleStartTrading}
          onViewPoints={handleViewPoints}
        />

        <CompletionModal
          isOpen={showFirstClaimModal}
          onClose={handleFirstClaimModalClose}
          mode="firstClaim"
          pointsEarned={firstClaimPoints}
          totalPointsEarned={0}
          onStartTrading={handleFirstClaimModalClose}
          onViewPoints={handleFirstClaimModalClose}
        />

        <MobileVerificationModal isOpen={showPhoneVerificationModal} onClose={handlePhoneVerificationClose} onVerificationSuccess={handlePhoneVerificationSuccess} />
      </div>
    </>
  );
};

export default HomePage;
